import type { Scene } from '../types'

export const deliveryScenes: Scene[] = [
  {
    id: 'DEL-01',
    chapter: 'delivery',
    route: '/delivery/scrum',
    title: 'Scrum as the Delivery Method',
    headline: 'Scrum: short cycles, working software, decisions made on evidence.',
    supporting:
      'LAMS is delivered with Scrum. Two-week sprints turn the approved Product Backlog into working increments that the District sees demonstrated every sprint and formally accepts at milestones.',
    visual: 'scrum-cycle',
    whyItMatters:
      'The District does not wait until the end of the project to find out whether LAMS works. It sees working functionality every two weeks, shapes what comes next through its business owners, and accepts formally only what has been demonstrated and tested.',
    sourceRefs: [
      { kind: 'literature', label: 'The Scrum Guide (Schwaber & Sutherland, 2020)' },
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.1-4.5, 5, 11.1' },
    ],
    presenterNotes: [
      'Position the method in one breath: Scrum is a lightweight framework — three accountabilities, five events, three artifacts — built around short, fixed-length sprints that each end in a usable increment.',
      'Then make it LAMS-specific. Two-week sprints; Sprint 0 confirms the baseline before kickoff; the Section 3 Product Backlog is the contracted scope baseline; every sprint runs refinement, design, implementation, testing, demonstration and feedback.',
      'Name the roles concretely: Felipe is the Scrum Master and the District\'s primary operational contact; District business owners set priorities and approve acceptance criteria; the Merlion team builds, tests and demonstrates.',
      'Close the distinction: sprint demonstrations give visibility; milestone acceptance gives formal commitment and releases payment.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Implementation & Project Management — Methodology'],
    evalTags: ['EVAL-WP', 'EVAL-TC'],
    exploreDetails: [
      {
        title: 'The Scrum framework',
        table: {
          head: ['Element', 'Scrum Guide', 'How it applies to LAMS'],
          rows: [
            ['Product Owner', 'Accountable for maximizing the value of the product and for ordering the Product Backlog.', 'Product ownership sits with the District: business owners and decision makers provide priorities and review and approve refined acceptance criteria.'],
            ['Scrum Master', 'Accountable for the team\'s effectiveness and for establishing Scrum.', 'Felipe Nonato Marinho, Project Manager / Scrum Master, project-long, and primary operational point of contact with the District.'],
            ['Developers', 'The people who create a usable increment each sprint.', 'The Merlion delivery team: architecture, engineering, GIS, data migration, QA, UX and documentation.'],
            ['Sprint', 'A fixed-length event in which all the work happens.', 'Two-week sprints: Sprint 0 for mobilization and baseline confirmation, then Sprints 1-16 through go-live and hypercare.'],
            ['Sprint Planning, Daily Scrum', 'Plan the sprint; inspect progress daily and adapt.', 'Sprint planning, progress and dependency tracking, and risk and issue management within project governance.'],
            ['Sprint Review', 'Inspect the increment with stakeholders and adapt the backlog.', 'Sprint demonstrations give the District regular visibility and feed back into dependent capabilities.'],
            ['Sprint Retrospective', 'Inspect how the team works and improve.', 'Continuous improvement of delivery practices, quality and flow.'],
            ['Product Backlog', 'The ordered list of what is needed to improve the product.', 'The Section 3 Product Backlog is the contracted functional baseline; changes go through formal change control.'],
            ['Increment', 'A usable step toward the product goal.', 'Working, tested functionality in Staging, accepted formally at milestones M1-M9.'],
          ],
        },
        sources: [
          { kind: 'literature', label: 'The Scrum Guide (Schwaber & Sutherland, 2020)' },
          { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.1-4.5, 5, 5.5, 11.1' },
        ],
      },
      {
        title: 'Sprint execution in LAMS',
        bullets: [
          'Each sprint follows a controlled cycle: backlog refinement, design, implementation, testing, demonstration and feedback.',
          'Quality assurance is incorporated throughout development — functional, integration, GIS, migration, security and regression testing as applicable — rather than deferred to the end.',
          'Discovery refines user stories, business rules and acceptance criteria without implicitly expanding the contracted scope.',
          'AI tools may assist engineering activities such as code analysis, test generation and documentation; human professionals remain responsible for every decision and deliverable.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.1-4.3' }],
      },
    ],
  },

  {
    id: 'DEL-02',
    chapter: 'delivery',
    route: '/delivery/approach',
    title: 'Delivery Strategy',
    headline: 'Progressive delivery with formal acceptance checkpoints.',
    supporting:
      'A 34-week program beginning January 1, 2027 — the District\'s own commencement date — delivered in two-week sprints with nine formal milestones.',
    visual: 'program-timeline',
    callouts: [
      { label: 'Commencement', detail: 'January 1, 2027' },
      { label: 'Formal kickoff', detail: 'January 15, 2027, after Sprint 0 baseline confirmation' },
      { label: 'UAT / production readiness', detail: 'August 12, 2027' },
      { label: 'Planned go-live', detail: 'August 13, 2027' },
      { label: 'Hypercare close', detail: 'August 26, 2027' },
    ],
    whyItMatters:
      'Sprint 0 exists so that the formal kickoff happens after requirements, backlog, architecture and dependencies have been confirmed with the District — not before.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 5, pp.11-15' },
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 2 — Commencement of work' },
    ],
    presenterNotes: [
      'Anchor on the District\'s date. January 1, 2027 is their schedule, not ours.',
      'Sprint 0 is the credibility move: we do not ask the District to accept a baseline we have not yet validated together.',
      'Note that the dates assume timely District access to subject-matter experts, environments, credentials, data sources and interfaces. That dependency is stated in the proposal, and it is fair to say so out loud.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Section 2 — Proposed Schedule', 'Implementation & Project Management — Methodology'],
    evalTags: ['EVAL-TC', 'EVAL-WP'],
  },

  {
    id: 'DEL-03',
    chapter: 'delivery',
    route: '/delivery/sprints',
    title: 'Sprint Roadmap',
    headline: 'Each increment produces visible business capability.',
    supporting:
      'Seventeen sprints across eight phases. Every phase ends with something the District can see working, not with a status report.',
    visual: 'sprint-roadmap',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 5.1, 5.2, pp.11-13' },
    ],
    presenterNotes: [
      'Do not read the sprint table. Walk the eight phases and stop.',
      'The shape matters more than the detail: foundation first, then the three business domains in the RFP\'s own order, then migration, then UAT, then go-live.',
      'Architecture, GIS, integration, migration and QA are cross-cutting workstreams running in parallel — they are not phases that wait their turn.',
    ],
    estimatedSeconds: 90,
    evalTags: ['EVAL-TC', 'EVAL-WP'],
    exploreDetails: [
      {
        title: 'Project phases',
        table: {
          head: ['Phase', 'Sprints', 'Primary objective'],
          rows: [
            ['Phase 0 — Mobilization and Baseline Confirmation', 'Sprint 0', 'Validate requirements, architecture, backlog, dependencies, environments, interfaces, data sources, acceptance approach and the implementation baseline.'],
            ['Phase 1 — Technical Foundation and Core Platform', 'Sprints 1-3', 'Establish security, shared data structures, application foundation, workflows, reporting, notifications, AI capability and integration foundations.'],
            ['Phase 2 — Land Acquisition', 'Sprints 4-6', 'Deliver the acquisition lifecycle from intake and GIS triage through evaluation, due diligence, approvals, closing and transition to Land Management.'],
            ['Phase 3 — Land Management', 'Sprints 7-10', 'Deliver planning, GIS-supported program management, operational work management, natural-resource activities, timber lifecycle, financial associations and supporting documents.'],
            ['Phase 4 — Land Disposition', 'Sprints 11-12', 'Deliver disposition initiation, GIS evaluation, LAS review, Committee and Board support, due diligence, closing and lifecycle completion.'],
            ['Phase 5 — Migration and System Consolidation', 'Sprints 13-14', 'Complete legacy-data conversion, reconciliation, end-to-end integration validation, regression testing and preparation of the UAT release candidate.'],
            ['Phase 6 — UAT, Training and Production Readiness', 'Sprint 15', 'Conduct District UAT, resolve acceptance issues, complete training and knowledge transfer, and perform the production-readiness review.'],
            ['Phase 7 — Production Deployment and Hypercare', 'Sprint 16', 'Execute approved production deployment, smoke validation, go-live and post-production hypercare.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 5.1' }],
      },
      {
        title: 'Full sprint plan',
        table: {
          head: ['Sprint', 'Dates', 'Work package', 'Planned deliverable'],
          rows: [
            ['Sprint 0', 'Jan 1-14, 2027', 'Mobilization, Discovery and Baseline Confirmation', 'Validated requirements baseline, confirmed backlog and sprint allocation, architecture confirmation, dependency register, environment-readiness plan, acceptance approach, kickoff readiness.'],
            ['Sprint 1', 'Jan 15-28', 'Platform Security and Data Foundation', 'Initial platform foundation with authentication, authorization, unified land-record model and core data-management capability.'],
            ['Sprint 2', 'Jan 29-Feb 11', 'Shared Workspace, Workflow and Migration Baseline', 'Unified workspace, shared workflow and task foundation, traceability framework, approved legacy-data assessment and source-to-target mapping baseline.'],
            ['Sprint 3', 'Feb 12-25', 'Operational Intelligence and Shared Services', 'Dashboards, reporting foundation, notifications, AI Information Assistant baseline, common integration monitoring and error handling.'],
            ['Sprint 4', 'Feb 26-Mar 11', 'Acquisition Intake and GIS Foundation', 'Acquisition intake, file management, ArcGIS operational foundation, GIS triage and map capability, CivicPlus intake integration.'],
            ['Sprint 5', 'Mar 12-25', 'Acquisition Planning and Evaluation', 'Land Management Prospectus, LAS ranking and score sheets, PaperVision document integration, template-based document generation foundation.'],
            ['Sprint 6', 'Mar 26-Apr 8', 'Acquisition Approval, Due Diligence and Closing', 'End-to-end acquisition increment through approvals, due diligence, closing, Land Management transition and Microsoft Word output.'],
            ['Sprint 7', 'Apr 9-22', 'Land Management Planning and Data Context', 'Three-year planning, GIS-integrated program planning and PERCH data integration.'],
            ['Sprint 8', 'Apr 23-May 6', 'Land Management Operations', 'Operational work planning, scheduling, maintenance activities, and work/contract/asset associations.'],
            ['Sprint 9', 'May 7-20', 'Natural Resource and Financial Management', 'Vegetation and natural-resource management, AccuFund financial-data integration.'],
            ['Sprint 10', 'May 21-Jun 3', 'Timber and Land Management Documentation', 'Timber management lifecycle and integrated land-management document preparation.'],
            ['Sprint 11', 'Jun 4-17', 'Disposition Initiation and Evaluation', 'Disposition initiation, Land Management transition, GIS triage and evaluation, LAS disposition review.'],
            ['Sprint 12', 'Jun 18-Jul 1', 'Disposition Approval, Due Diligence and Closing', 'Complete disposition workflow through Committee and Board support, due diligence, closing and lifecycle completion.'],
            ['Sprint 13', 'Jul 2-15', 'Legacy Data Conversion and End-to-End Integration', 'Executed migration conversion, integration consolidation, migration dry runs, end-to-end technical validation.'],
            ['Sprint 14', 'Jul 16-29', 'Migration Reconciliation and System Hardening', 'Reconciled migration results, exception processing, full regression cycle, defect remediation, Staging/UAT release candidate.'],
            ['Sprint 15', 'Jul 30-Aug 12', 'District UAT, Training and Production Readiness', 'District acceptance testing, resolved acceptance defects, role-based training, Quick Steps and operational documentation, knowledge transfer, Go/No-Go package.'],
            ['Sprint 16', 'Aug 13-26', 'Production Deployment, Go-Live and Hypercare', 'Approved production deployment, smoke validation, operational go-live, monitoring, issue stabilization, hypercare completion.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 5.2' }],
      },
    ],
  },

  {
    id: 'DEL-04',
    chapter: 'delivery',
    route: '/delivery/milestones',
    title: 'Milestones M1-M9',
    headline: 'Nine checkpoints connect progress, evidence and acceptance.',
    supporting:
      'Formal acceptance is attached to major implementation increments, each with defined acceptance criteria and supporting evidence — not to hours worked.',
    visual: 'milestone-rail',
    whyItMatters:
      'Each milestone is a decision point where the District sees working capability and decides whether it is acceptable. Payment follows that decision.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 5.3, pp.13-14' },
    ],
    presenterNotes: [
      'Point at M7, M8 and M9. Only 10% of the price is paid across production readiness, go-live and hypercare — deliberately, so our final payment depends on production stabilization.',
      'Acceptance of a milestone does not waive correction of defects found later. Say so; it removes a natural objection.',
    ],
    estimatedSeconds: 75,
    evalTags: ['EVAL-TC', 'EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'Milestone acceptance criteria',
        table: {
          head: ['Milestone', 'Target date', 'Acceptance'],
          rows: [
            ['M1 — Implementation Baseline and Formal Kickoff', 'January 14-15, 2027 / Sprint 0', 'Principal requirements reviewed; backlog and initial sprint allocation confirmed; architecture baseline validated; integration, data, environment, dependency and acceptance assumptions documented; project authorized to proceed.'],
            ['M2 — Core Platform Baseline', 'February 25, 2027 / Sprint 3', 'Core platform demonstrated and internally validated: identity and access, unified land record, shared workflow, operational views, reporting foundation, notifications, traceability and common platform services.'],
            ['M3 — Land Acquisition Increment', 'April 8, 2027 / Sprint 6', 'Acquisition lifecycle demonstrated in Staging: intake, GIS triage, prospectus, LAS evaluation, approval support, due diligence, closing, document generation and transition to Land Management.'],
            ['M4 — Land Management Increment', 'June 3, 2027 / Sprint 10', 'Land Management demonstrated in Staging: planning, GIS-supported program management, operational activities, natural-resource management, timber lifecycle, contract and asset associations, financial context and document preparation.'],
            ['M5 — Land Disposition Increment', 'July 1, 2027 / Sprint 12', 'Disposition lifecycle demonstrated in Staging: initiation, GIS evaluation, LAS review, Committee and Board support, due diligence, closing and lifecycle completion.'],
            ['M6 — Migration and System Consolidation', 'July 29, 2027 / Sprint 14', 'Required integrations validated end-to-end; legacy-data migration executed and reconciled; exceptions documented and processed; regression testing completed; Staging/UAT release candidate approved.'],
            ['M7 — UAT and Production Readiness', 'August 12, 2027 / Sprint 15', 'District UAT completed; acceptance defects resolved or dispositioned by agreement; training and knowledge transfer completed; production-readiness checklist reviewed; Go/No-Go decision approved.'],
            ['M8 — Production Go-Live', 'August 13, 2027 / Sprint 16', 'Approved release deployed to Production; deployment and migration activities completed; production smoke validation performed; system released for operational use.'],
            ['M9 — Hypercare Completion and Final Implementation Acceptance', 'August 26, 2027 / Sprint 16', 'Initial production stabilization completed; critical go-live issues resolved; operational handoff completed; agreed deliverables accepted and project transitioned to the support model.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 5.3' }],
      },
    ],
  },

  {
    id: 'DEL-05',
    chapter: 'delivery',
    route: '/delivery/governance',
    title: 'Governance & Stakeholders',
    headline: 'Delivery is a shared governance process.',
    supporting:
      'The District brings decision authority and domain knowledge; Merlion brings a named delivery organization. Both sides of the boundary are written down.',
    visual: 'stakeholder-map',
    whyItMatters:
      'Most implementation delay is not technical. It comes from unclear ownership of decisions, environments and data. Naming both sides early is how that is prevented.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.5, 7.1, 11.1' },
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Implementation & Project Management' },
    ],
    presenterNotes: [
      'The RFP asks for a project manager committed to a teamwork atmosphere, transparent communications, timeliness, responsiveness and integrity. Felipe is named and assigned project-long.',
      'The responsibilities matrix in the proposal is the artefact behind this scene. Offer it rather than reading it.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Implementation & Project Management — Project Manager', 'Implementation & Project Management — Methodology'],
    evalTags: ['EVAL-WP', 'EVAL-STAFF'],
    exploreDetails: [
      {
        title: 'Merlion delivery organization',
        table: {
          head: ['Responsibility area', 'Primary roles', 'Responsibility'],
          rows: [
            ['Project Governance and Requirements', 'Project Manager / Scrum Master; Requirements Analyst', 'Schedule, backlog, stakeholder coordination, scope, decisions, risks, requirements refinement, acceptance coordination and reporting.'],
            ['Architecture and Engineering', 'Software Architect / Technical Lead; Fullstack Developer; Infrastructure Analyst', 'Application architecture, data and API design, frontend and backend implementation, security, environments, deployment and technical quality.'],
            ['GIS, Data and Integration', 'GIS Analyst; Data Migration / ETL Specialist; Software Architect', 'ArcGIS integration, GIS-supported workflows, source-data assessment, migration, reconciliation and enterprise-system integrations.'],
            ['Quality, Experience and Readiness', 'QA / Test Engineer; UX/UI Designer; Training / Documentation Specialist', 'Test planning and execution, regression and acceptance support, usability, training materials, knowledge transfer and operational readiness.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 7.1' }],
      },
      {
        title: 'District stakeholder groups represented in governance',
        bullets: [
          'Project and product leadership',
          'Land Acquisition',
          'Land Management',
          'GIS',
          'IT, infrastructure and security',
          'Finance',
          'Records and documents',
          'UAT and operational users',
          'Leadership and approval stakeholders as applicable',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.5, 11.1' }],
      },
    ],
  },

  {
    id: 'DEL-06',
    chapter: 'delivery',
    route: '/delivery/migration-quality',
    title: 'Quality, Migration and Acceptance',
    headline: 'Migration is not complete until it is reconciled.',
    supporting:
      'The District accepts source-to-target reconciliation, documented exceptions, corrective processing and District sign-off as the acceptance standard for the zero-error requirement.',
    visual: 'migration-pipeline',
    whyItMatters:
      'A zero-error rate cannot be demonstrated by asserting it. It is demonstrated by counting records at both ends, reporting every exception, resolving each one explicitly, and having the District sign.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Sections 5.F-5.H, p.8' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q15, p.2' },
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.4, 6.4' },
    ],
    presenterNotes: [
      'This is the strongest single answer to the Outcomes and Performance criterion. Spend the time.',
      'Quote the Q&A: we asked whether a jointly approved validation and reconciliation process would be accepted as the standard for the zero-error requirement. The District answered "Yes."',
      'Emphasize that exceptions are dispositioned explicitly rather than silently carried into production. That is the difference between a reconciled migration and a hopeful one.',
      'Repeatable migration means we can dry-run before the real cutover and show the District measurable evidence beforehand.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: [
      'Section 5.F — Data Migration, zero-error rate',
      'Section 5.G — Program Testing',
      'Section 5.H — Training',
    ],
    evalTags: ['EVAL-OUTCOME', 'EVAL-WP'],
    exploreDetails: [
      {
        title: 'Migration method',
        body: [
          'Source Assessment → Mapping → Transformation → Migration → Reconciliation → Exception Resolution → District Validation.',
          'Activities include source-data profiling, source-to-target mapping, transformation rules, controlled imports, reconciliation between source and destination records, exception reporting, corrective processing, and District review and sign-off.',
          'Migration execution uses repeatable processes so test migrations can be performed before the final production migration, reducing cutover risk and giving the District measurable evidence of completeness and accuracy before final acceptance.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 6.4' }],
      },
      {
        title: 'What is migrated, and what is not',
        table: {
          head: ['Source', 'Treatment'],
          rows: [
            ['Land Acquisition & Surplus Access database', 'Migrated. 1,024 records across 30 fields, plus parcel records. The District indicated the database would ideally be replaced by this solution.'],
            ['GIS data', 'Continues to be maintained in the District ArcGIS environment; referenced rather than copied.'],
            ['PaperVision Enterprise documents', 'Not migrated. The District confirmed documents in PaperVision are not being migrated into the new system; LAMS links to them via the PaperVision API.'],
            ['PERCH', 'Not migrated. Accessed through District-provided read-only SQL Server views.'],
            ['AccuFund', 'Not migrated. Applicable financial information incorporated through the approved exchange mechanism.'],
            ['Legacy PDFs and Excel documents in network folders', 'Assessed during Sprint 0 and Sprint 2; converted or linked according to the confirmed mapping baseline.'],
          ],
        },
        sources: [
          { kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q4, Q5, Q26' },
          { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 6.4' },
        ],
      },
      {
        title: 'Testing, UAT and training',
        bullets: [
          'Vendor testing during development, with correction of issues identified during the development review period.',
          'District staff user testing is supported, with feedback collected for further system corrections, and additional testing to confirm corrections were properly made.',
          'The proposer provides the UAT environment, as the District clarified.',
          'A full regression cycle runs in Sprint 14 before the Staging/UAT release candidate is offered.',
          'Training includes written and online materials and "Quick Steps" guides for identified key priority tasks, delivered in Sprint 15 with knowledge transfer.',
        ],
        sources: [
          { kind: 'rfp', label: 'SRWMD RFP 26/27-002', locator: 'Sections 5.G, 5.H' },
          { kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q27' },
          { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.4, 4.6' },
        ],
      },
    ],
  },

  {
    id: 'DEL-07',
    chapter: 'delivery',
    route: '/delivery/risks',
    title: 'Risk Management',
    headline: 'Major implementation risks are identified before they become schedule surprises.',
    supporting:
      'The RFP asks for identified risks and constraints with mitigation recommendations. These are the eight we consider material for this project.',
    visual: 'risk-matrix',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Evaluation and Award — identified risks and constraints with mitigation' },
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Sections 4.7, 11.2, 11.3' },
    ],
    presenterNotes: [
      'Do not soften these. Naming real risks with concrete mitigations scores better than claiming a project has none.',
      'Two are genuinely outside our control — District stakeholder availability and environment readiness. Say so plainly and point to Sprint 0 as where they get managed.',
      'The four-VPN-licence constraint is worth mentioning if the audience includes IT.',
    ],
    estimatedSeconds: 90,
    rfpAlignment: ['Evaluation and Award — identified risks and constraints with mitigation recommendations'],
    evalTags: ['EVAL-WP', 'EVAL-TC'],
    exploreDetails: [
      {
        title: 'Risk register',
        table: {
          head: ['Risk', 'Mitigation'],
          rows: [
            ['Source data quality and migration exceptions', 'Early profiling in Sprint 2, source-to-target mapping, repeatable dry runs, reconciliation with explicit exception disposition and District sign-off.'],
            ['Integration and API availability', 'Confirm interfaces in Sprint 0; adapter-based isolation per system; monitoring and traceable error handling; fallback exchange patterns where approved. AccuFund CSV feasibility is explicitly yet to be determined by the District.'],
            ['GIS dependency and configuration', 'Early ArcGIS validation; provider abstraction so the business model is not coupled to a layer or table; a GIS workstream running throughout the project rather than a single phase.'],
            ['District stakeholder availability', 'Planned review windows, named backlog ownership, sprint reviews and early escalation through project governance.'],
            ['Environment and access readiness', 'Sprint 0 environment checklist and dependency register. The District indicated no more than four VPN licences will be available for secure remote implementation access.'],
            ['Scope refinement and scope creep', 'Baseline confirmation at M1, backlog traceability, and formal change control for anything that materially alters contracted scope.'],
            ['UAT timing and acceptance defects', 'Acceptance criteria defined early with each backlog item, continuous sprint demos, staged regression in Sprint 14, and a defined Go/No-Go decision at M7.'],
            ['Adoption and operational readiness', 'Role-based training, Quick Steps guides for key priority tasks, knowledge transfer in Sprint 15 and hypercare through Sprint 16.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 4.7' }],
      },
    ],
  },

  {
    id: 'DEL-08',
    chapter: 'delivery',
    route: '/delivery/outcomes',
    title: 'Outcomes and Performance Measures',
    headline: 'LAMS is measured by the outcomes it enables, not only by the features it ships.',
    supporting:
      'Ten outcomes, each with a KPI and a measurement method agreed with the District. Baselines are confirmed in Sprint 0, and measurements distinguish vendor-controlled performance from external dependencies.',
    visual: 'outcomes-grid',
    whyItMatters:
      'Final acceptance does not rest on a feature checklist. It considers milestone criteria, UAT results, migration reconciliation, training readiness, production validation and a completed hypercare period — the evidence that LAMS is working in operation.',
    sourceRefs: [
      { kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 8' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q91, p.11' },
    ],
    presenterNotes: [
      'This answers the Outcomes and Performance Measurements criterion directly. Walk the grid by theme: efficiency and data, delivery quality, readiness and adoption, stability and improvement.',
      'Be precise about the 40%: the proposal supports the District\'s objective of roughly 40% less manual effort, with the baseline and method confirmed in Sprint 0 — an operational objective, not a stand-alone contractual threshold. The Q&A says the same.',
      'Point out the fairness built in: measurements separate what Merlion controls from what depends on District participation, source-data quality or third-party systems.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Outcomes and Performance Measurements'],
    evalTags: ['EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'Outcomes, KPIs and measurement methods',
        table: {
          head: ['Outcome', 'KPI', 'Target / measurement method'],
          rows: [
            ['Reduced manual effort', 'Reduction in identified repetitive or manual activities', 'Supports the District\'s objective of approximately 40% reduction in manual effort for applicable processes; baseline and method confirmed in Sprint 0; an operational objective rather than a stand-alone contractual threshold.'],
            ['Improved data reliability', 'Data validation and consistency results', 'Required validation rules implemented; data-quality exceptions tracked and resolved through controlled processes.'],
            ['Migration accuracy', 'Source-to-target reconciliation', 'Migrated records reconciled against approved source populations; exceptions documented, corrected or dispositioned with District approval.'],
            ['Process automation', 'Percentage of agreed workflow steps supported by LAMS', 'Required workflows implemented according to the approved Product Backlog and validated acceptance criteria.'],
            ['Testing and release quality', 'Acceptance and release results', 'Measured against acceptance criteria, the defect-management process and the release-performance commitments.'],
            ['Integration reliability', 'Successful execution and traceability of required interfaces', 'Required integrations operational and monitored; failed or incomplete exchanges traceable for investigation and correction.'],
            ['User readiness', 'Training participation and readiness completion', 'Training materials delivered; user and key-user sessions completed before production-readiness approval.'],
            ['Operational adoption', 'Successful use of core workflows after go-live', 'Core processes available and functioning in Production during hypercare; adoption issues tracked and addressed.'],
            ['Production stability', 'Critical production issues during hypercare', 'Critical go-live issues identified, prioritized and resolved before final implementation acceptance.'],
            ['Continuous improvement', 'Identified and governed improvement opportunities', 'Documented through backlog, feedback, testing, support and approved AI-assisted analysis; changes remain under governance and release control.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Implementation Plan', locator: 'Section 8' }],
      },
    ],
  },

  {
    id: 'DEL-09',
    chapter: 'delivery',
    route: '/delivery/support-model',
    title: 'Technical Support Model',
    headline: 'Go-live begins operations — it does not end the relationship.',
    supporting:
      'Twelve months of Level 1 and Level 2 support from Final Implementation Acceptance, Monday to Friday, 8:00 a.m. to 5:00 p.m. Eastern Time. Every request is a ticket; urgent incidents can also be raised by phone.',
    visual: 'support-model',
    whyItMatters:
      'Every support request — including a phone call — becomes a ticket with a severity, an owner and a measured SLA. The District gets a traceable record of every incident and a monthly view of how support is actually performing.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.E — Technical Support' },
      { kind: 'proposal', label: 'Merlion Support Model', locator: 'Sections 10.1-10.5, 10.8' },
    ],
    presenterNotes: [
      'Two levels, clearly separated. Level 1 is the service desk: registration, severity classification, initial diagnosis, user guidance and escalation. Level 2 is technical and application support: troubleshooting, integration analysis, defect correction and corrective releases.',
      'Channels follow severity. The Support Portal is always the record. Telephone is available and recommended for critical incidents, and for high-severity incidents identified as urgent when opened — and a phone call still creates a ticket.',
      'Severity 1 goes straight to Level 2 while Level 1 keeps the District informed.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Section 5.E — Technical Support'],
    evalTags: ['EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'Support levels',
        table: {
          head: ['Level', 'Scope', 'Typical activities'],
          rows: [
            ['Level 1 — Service Desk and Functional Support', 'Initial point of contact for District users and operational support requests.', 'Ticket registration, severity classification, initial diagnosis, user guidance, known-issue resolution, configuration verification, collection of diagnostic evidence, communication and escalation.'],
            ['Level 2 — Technical and Application Support', 'Specialized technical investigation for issues not resolved at Level 1 or requiring engineering expertise.', 'Application troubleshooting, integration analysis, database and application diagnostics, infrastructure analysis for vendor-managed components, defect investigation, corrective maintenance and corrective release preparation.'],
          ],
        },
        body: [
          'Severity 1 incidents may be escalated directly and immediately to Level 2 while Level 1 maintains coordination and communication with the District. Engineering resources may be engaged by Level 2 when source-code correction is required; this does not create an additional commercial support level.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Section 10.3' }],
      },
      {
        title: 'Support channels by severity',
        table: {
          head: ['Severity', 'Support Portal', 'Telephone', 'Handling'],
          rows: [
            ['S1 — Critical', 'Required — a ticket must exist', 'Available and recommended', 'Immediate Level 1 coordination and Level 2 technical engagement'],
            ['S2 — High', 'Required — a ticket must exist', 'Available when identified as urgent at opening', 'Prioritized Level 1 assessment with Level 2 escalation'],
            ['S3 — Medium', 'Required', 'Not an initial channel', 'Normal Level 1 / Level 2 workflow'],
            ['S4 — Low / Service Request', 'Required', 'Not applicable', 'Standard service-request workflow'],
          ],
        },
        body: [
          'Unless optional after-hours coverage is contracted, tickets received outside the support window are recorded when submitted, and the SLA clock starts at the beginning of the next support window.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Sections 10.2, 10.5' }],
      },
      {
        title: 'SLA reporting and escalation',
        bullets: [
          'Tickets opened and closed, and tickets by severity.',
          'SLA response compliance, and restoration and resolution performance.',
          'Recurring incidents, unresolved or aging tickets, and incidents involving third-party dependencies.',
          'Repeated or material SLA exceptions are reviewed through operational governance with corrective actions. The detailed escalation matrix and contacts are finalized during the transition from hypercare to support.',
        ],
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Section 10.8' }],
      },
    ],
  },

  {
    id: 'DEL-10',
    chapter: 'delivery',
    route: '/delivery/support',
    title: 'Severity Levels and Service Targets',
    headline: 'Four severity levels, each with a measurable service target.',
    supporting:
      'Every incident is classified by its operational impact. Each severity carries an initial-response, technical-engagement and restoration target, measured within the support window.',
    visual: 'severity-sla',
    whyItMatters:
      'A critical production incident gets a response within 30 minutes and restoration or a workaround within four business hours. The targets are specific enough to be measured — and they are reported.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 5.E — Technical Support' },
      { kind: 'proposal', label: 'Merlion Support Model', locator: 'Section 10, pp.27-29' },
    ],
    presenterNotes: [
      'The District asked about technical support and help desk solutions in Section 5.E. This is the answer, with measurable targets. Read the S1 row aloud; point at the others.',
      'Severity can be adjusted by mutual agreement when the actual operational impact differs from what was first reported.',
      'Be clear about the boundary: support covers operation and maintenance of the accepted scope. New functionality goes through change control. Saying this now prevents a dispute later.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 5.E — Technical Support'],
    evalTags: ['EVAL-OUTCOME', 'EVAL-TC'],
    exploreDetails: [
      {
        title: 'Service level agreement',
        table: {
          head: ['Severity', 'Initial response', 'Technical engagement', 'Restoration / workaround', 'Resolution'],
          rows: [
            ['S1 — Critical', '30 minutes', 'Within 30 minutes', '4 business hours', 'Continuous prioritized effort through restoration; permanent correction as soon as technically practicable.'],
            ['S2 — High', '1 business hour', 'Within 2 business hours', '8 business hours', '2 business days where technically practicable.'],
            ['S3 — Medium', '4 business hours', 'Within 1 business day', 'As applicable', '3 business days or agreed corrective release.'],
            ['S4 — Low / Service Request', '1 business day', 'As applicable', 'Not applicable', '5 business days, scheduled maintenance release, or agreed service date.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Section 10.6' }],
      },
      {
        title: 'Severity definitions',
        table: {
          head: ['Severity', 'Definition'],
          rows: [
            ['S1 — Critical', 'Production service unavailable, a critical business process unavailable for multiple users, significant data-integrity or security risk, or another condition preventing essential LAMS operations with no reasonable workaround.'],
            ['S2 — High', 'Major functionality unavailable or materially degraded, affecting significant business activity or multiple users, with no adequate or only limited workaround.'],
            ['S3 — Medium', 'Non-critical functionality affected, limited users impacted, or a defect with a reasonable workaround. Normal business operation can continue.'],
            ['S4 — Low / Service Request', 'Minor defect, usability issue, information request, how-to question, configuration assistance, or other request not materially affecting business operations.'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Section 10.4' }],
      },
      {
        title: 'Included, and not included',
        table: {
          head: ['Service', 'Status'],
          rows: [
            ['Service desk and ticket management', 'Included in annual support'],
            ['Incident management and corrective maintenance', 'Included'],
            ['Application and integration support', 'Included'],
            ['Application monitoring for vendor-managed services', 'Included'],
            ['Security and dependency maintenance for vendor-managed components', 'Included'],
            ['SLA reporting', 'Included'],
            ['New functional development, new modules, new integrations, major data-conversion projects', 'Change request or separately authorized professional services'],
            ['Extended or after-hours coverage', 'Optional, separately priced if requested'],
          ],
        },
        sources: [{ kind: 'proposal', label: 'Merlion Support Model', locator: 'Sections 10.7, 10.9, 10.10' }],
      },
    ],
  },
]
